import { ChevronDown, ChevronUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { CableTier, NetworkResponse } from '../api'
import { NODE_COLOR, NODE_KIND_SHORT, TIER_COLOR, TIER_LABEL, fmtM } from '../format'

/** Legend doubles as the cable-tier layer switch, with real km per tier. */
export function Legend({
  network,
  tiers,
  onToggle,
  defaultOpen = true,
}: {
  network: NetworkResponse
  tiers: Record<CableTier, boolean>
  onToggle: (t: CableTier) => void
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  // Follow the layout: collapse when the window narrows to a phone, so it stops covering the map.
  useEffect(() => setOpen(defaultOpen), [defaultOpen])
  const km = network.summary.kmByTier
  const kinds = [...new Set(network.nodes.map((n) => n.kind))]
  return (
    <div className="pointer-events-auto w-56 rounded-xl border border-border bg-surface text-fg shadow-panel">
      <button
        className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        Legend and layers
        {open ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronUp className="h-4 w-4" aria-hidden />}
      </button>
      {open && (
        <div className="space-y-2 px-3 pb-3">
          <fieldset>
            <legend className="sr-only">Cable tiers shown on the map</legend>
            {(Object.keys(TIER_LABEL) as CableTier[]).map((t) => (
              <label key={t} className="flex cursor-pointer items-center gap-2 py-0.5 text-sm">
                <input type="checkbox" checked={tiers[t]} onChange={() => onToggle(t)} className="h-4 w-4 accent-[var(--accent)]" />
                <span className="inline-block h-1.5 w-5 rounded" style={{ background: TIER_COLOR[t] }} aria-hidden />
                <span className="flex-1">{TIER_LABEL[t]}</span>
                <span className="num text-xs text-muted">{km[t] ? fmtM(km[t]! * 1000) : '—'}</span>
              </label>
            ))}
          </fieldset>
          <div className="flex flex-wrap gap-x-3 gap-y-1 border-t border-border pt-2 text-xs">
            {kinds.map((k) => (
              <span key={k} className="inline-flex items-center gap-1">
                <span className="inline-block h-3 w-3 rounded-full border-2 border-white" style={{ background: NODE_COLOR[k] }} aria-hidden />
                {NODE_KIND_SHORT[k]}
              </span>
            ))}
            <span className="inline-flex items-center gap-1">
              <span className="inline-block h-3 w-3 rounded-full border-[3px] border-[#dc2626] bg-[#15803d]" aria-hidden />
              Has an issue
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
